import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useListRestoreStore } from '/src/context';
import { RestorableListState } from '/src/context/stores/listRestoreStore';

type UseRestorableListStateConfig<TState extends RestorableListState> = {
  surfaceId: string;
  defaults: TState;
  standalone?: boolean;
};

type UseRestorableListStateReturn<TState extends RestorableListState> = {
  state: TState;
  setState: (updates: Partial<TState>) => void;
  resetToDefaults: () => void;
  restoreKey: string;
  isRestored: boolean;
};

export function useRestorableListState<TState extends RestorableListState>(
  config: UseRestorableListStateConfig<TState>
): UseRestorableListStateReturn<TState> {
  const { surfaceId, defaults, standalone = false } = config;
  const location = useLocation();
  const navigationType = useNavigationType();
  const listRestoreStore = useListRestoreStore();

  const restoreKey = `${location.pathname}::${surfaceId}`;
  const isPop = navigationType === 'POP';

  const restoredState = isPop ? listRestoreStore.getState(restoreKey) : undefined;
  const initialState = restoredState ? ({ ...defaults, ...restoredState } as TState) : defaults;

  const [state, setStateInternal] = useState<TState>(initialState);
  const prevRestoreKeyRef = useRef(restoreKey);
  const justRekeyedRef = useRef(false);

  if (prevRestoreKeyRef.current !== restoreKey) {
    prevRestoreKeyRef.current = restoreKey;
    justRekeyedRef.current = true;
    const nextRestored = isPop ? listRestoreStore.getState(restoreKey) : undefined;
    setStateInternal(nextRestored ? ({ ...defaults, ...nextRestored } as TState) : defaults);
  }

  const isRestored = isPop && !!restoredState;

  useLayoutEffect(() => {
    if (isRestored && standalone) {
      listRestoreStore.signalRestore(location.pathname);
    }
  }, []);

  const hasPersistedRef = useRef(false);

  const setState = useCallback((updates: Partial<TState>) => {
    setStateInternal((prev) => {
      const next = { ...prev, ...updates };
      return next;
    });
  }, []);

  const resetToDefaults = useCallback(() => {
    listRestoreStore.clearState(restoreKey);
    setStateInternal(defaults);
  }, [listRestoreStore, restoreKey, defaults]);

  useEffect(() => {
    if (isRestored && !hasPersistedRef.current) {
      hasPersistedRef.current = true;
      return;
    }
    if (justRekeyedRef.current) {
      justRekeyedRef.current = false;
      return;
    }
    hasPersistedRef.current = true;
    listRestoreStore.setState(restoreKey, state);
  }, [state, listRestoreStore, restoreKey]);

  return {
    state,
    setState,
    resetToDefaults,
    restoreKey,
    isRestored,
  };
}

export function useSyncDraft<TState, TDraft>(
  state: TState,
  setDraft: (next: TDraft) => void,
  select: (state: TState) => TDraft
): void {
  const setDraftRef = useRef(setDraft);
  const selectRef = useRef(select);
  setDraftRef.current = setDraft;
  selectRef.current = select;

  useEffect(() => {
    setDraftRef.current(selectRef.current(state));
  }, [state]);
}
